// frontend/src/features/citysense/housing-stock-modeller/components/OptimizerPanel.tsx

import React, { useState } from "react";
import { OptimizerResult } from "../types";
import { Play, TrendingUp, Euro, Home, Zap } from "lucide-react";

interface Props {
  archetypes: string[];
  onRun: (base: string, target: string, minDI: string) => OptimizerResult;
}

export const OptimizerPanel: React.FC<Props> = ({ archetypes, onRun }) => {
  const [baseArchetype, setBaseArchetype] = useState(archetypes[0] || "");
  const [targetArchetype, setTargetArchetype] = useState(
    "Post-2000 - Natural gas - Medium (3-5 rooms)",
  );
  const [minDeprivation, setMinDeprivation] = useState(
    "Marginally Above Average",
  );
  const [result, setResult] = useState<OptimizerResult | null>(null);

  const deprivationOptions = [
    "Very Disadvantaged",
    "Disadvantaged",
    "Marginally Below Average",
    "Marginally Above Average",
    "Affluent",
    "Very Affluent",
  ];

  const handleRun = () => {
    const res = onRun(baseArchetype, targetArchetype, minDeprivation);
    setResult(res);
  };

  // Fix broken diamond characters
  const cleanArchetypeName = (name: string) => name.replace(/�/g, "◆");

  return (
    <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-2 border-amber-200 rounded-xl p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Zap className="w-6 h-6 text-amber-600" />
        Housing Retrofit Optimizer
      </h3>

      <div className="space-y-4 mb-5">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Base Archetype (to upgrade from):
          </label>
          <select
            value={baseArchetype}
            onChange={(e) => setBaseArchetype(e.target.value)}
            className="w-full p-3 text-sm border-2 border-gray-300 rounded-lg bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
          >
            {archetypes.map((arch) => (
              <option key={arch} value={arch}>
                {cleanArchetypeName(arch)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Target Archetype (upgrade to):
          </label>
          <select
            value={targetArchetype}
            onChange={(e) => setTargetArchetype(e.target.value)}
            className="w-full p-3 text-sm border-2 border-gray-300 rounded-lg bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
          >
            {archetypes.map((arch) => (
              <option key={arch} value={arch}>
                {cleanArchetypeName(arch)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Minimum Deprivation Index (affordability):
          </label>
          <select
            value={minDeprivation}
            onChange={(e) => setMinDeprivation(e.target.value)}
            className="w-full p-3 text-sm border-2 border-gray-300 rounded-lg bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
          >
            {deprivationOptions.map((di) => (
              <option key={di} value={di}>
                {di}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleRun}
          className="w-full flex items-center justify-center gap-2 p-3 text-sm font-bold bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 shadow-md hover:shadow-lg transition-all"
        >
          <Play className="w-4 h-4" />
          RUN OPTIMIZER
        </button>
      </div>

      {result ? (
        <div className="bg-white rounded-lg p-5 space-y-4 border-2 border-amber-200">
          <p className="text-xs text-gray-600 border-b border-gray-200 pb-3">
            Upgraded <strong>{result.upgradedPolygons.size}</strong> polygons —
            see ⭐ markers on map
          </p>

          <ResultRow
            label="Dwellings Affected"
            value={result.dwellingsAffected.toLocaleString()}
            icon={<Home className="w-5 h-5 text-teal-600" />}
          />
          <ResultRow
            label="Total Retrofit Cost"
            value={`€${(result.totalCost / 1000000).toFixed(1)}M`}
            icon={<Euro className="w-5 h-5 text-red-600" />}
          />
          <ResultRow
            label="GHG Savings (20yr)"
            value={`${(result.totalGHGSavings / 1000).toFixed(1)}k tCO₂e`}
            icon={<TrendingUp className="w-5 h-5 text-green-600" />}
          />

          <p className="text-xs text-gray-600 pt-3 border-t border-gray-200">
            💡 Results applied to map. Polygons meeting affordability criteria
            have been upgraded.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg p-5 text-sm text-gray-600 leading-relaxed border-2 border-gray-200">
          <p className="mb-2">
            <strong>How it works:</strong>
          </p>
          <p>
            Select a base archetype, target archetype, and minimum deprivation
            index. The optimizer will upgrade all eligible polygons (matching
            base archetype and affordability criteria).
          </p>
        </div>
      )}
    </div>
  );
};

const ResultRow: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
}> = ({ label, value, icon }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-xs font-semibold text-gray-600">{label}</span>
    </div>
    <span className="text-lg font-bold text-gray-800">{value}</span>
  </div>
);